import { StyledAlert, WrapLgMarginBottom } from "App/global-style";
import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPayment,
  getPaymentRestriction,
} from "Store/akaAdvice/interfaces/getters";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "../../interfaces";
import { ContentWrapper, MarginLeftWrapper } from "../../styled";

import CollapsibleContainer from "Components/CollapsibleContainer";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { ProductCoverages } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/ProductCoverages";
import { useAdviceDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { mapInsuranceNameToAdviceCoverageName } from "Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { getPriceFormatFromValue } from "Formatting/index";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAkaAdviceBaseAmountSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { akaAdvicePriceCalculationRequestStart } from "Store/akaAdvice/slice";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useIsAkademikerneBranding } from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { AdviceCardComment } from "../../AdviceCardComment";
import { AdviceCardHeader } from "../../AdviceCardHeader";
import { CardTotalPrice } from "../../AdvicePrice/CardTotalPrice";
import { AdvisorUwInfo } from "../../AdvisorUwInfo";
import { AkaAdviceChartDisability } from "../../AkaAdviceCharts/AkaAdviceChartDisability";
import { usePriceStatus } from "../../hooks/usePriceStatus";
import { AkaGroupLifeIncapacityOfWorkCoInsuredForm } from "../Forms/AkaGroupLifeIncapacityOfWorkCoInsuredForm";
import { AkaGroupLifeIncapacityOfWorkForm } from "../Forms/AkaGroupLifeIncapacityOfWorkForm";
import { AkaGroupLifeTimeLimitedDisabilityInterestCoInsuredForm } from "../Forms/AkaGroupLifeTimeLimitedDisabilityInterestCoInsuredForm";
import { AkaGroupLifeTimeLimitedDisabilityInterestForm } from "../Forms/AkaGroupLifeTimeLimitedDisabilityInterestForm";
import { DisabilityForm } from "../Forms/DisabilityForm";
import { DisabilityOneTimePaymentForm } from "../Forms/DisabilityOneTimePayment/DisabilityOneTimePaymentForm";
import { DisabilityPaymentAfter3Months } from "../Forms/DisabilityOneTimePayment/DisabilityPaymentAfter3Months";
import { AkaDisabilityAdviceText } from "../Texts/AkaDisabilityAdviceText";

const NEED_NAME = NeedName.DISABILITY;

export interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceCard: any;
  lang: TextLanguage;
}

export const AkaDisabilityAdviceCard = ({
  formProps,
  need,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAkaBranding = useIsAkademikerneBranding();
  const dispatch = useDispatch();
  const adviceDetails = useAdviceDetailsFormValues();
  const isAdvisor = useIsAdvisorSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const selected = formProps.values.disabilityCheckbox;
  const currentSliderValue = formProps.values.disabilityInsuredSumSlider;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const insuranceType = adviceCard.insuranceType[lang];

  const recalculatePrice = (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ): void => {
    setPriceIsChanging(false);
    setPriceIsLoading(true);
    dispatch(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice: isAkaBranding,
        priceFormValues: formValues,
        specificProduct,
      }),
    );
  };

  if (!adviceDetails) {
    return null;
  }

  const adviceValueAkaGroupLifeIncapacity = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
    isAdvisor,
  ).adviceValue;
  const effectivePaymentRateAkaGroupLifeIncapacity = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
  ).effectivePaymentRate;

  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );

  const recommendedInsuranceSumAkaGroupLifeDeath = Math.trunc(
    adviceValueAkaGroupLifeIncapacity *
      baseAmount *
      effectivePaymentRateAkaGroupLifeIncapacity,
  );

  return (
    <div className="AdviceCard_DISABILITY_FLEX">
      <DetailsCardWrapper
        key={insuranceType}
        adviceDetailsCard
        selected={selected}
      >
        <AdviceCardHeader
          insuranceId={AkaAdviceCheckboxes.DISABILITY_CHECKBOX}
          insuranceType={insuranceType}
        />
        <ContentWrapper className="AdviceCardContent">
          <p className="AdviceHeaderText">
            En uføreforsikring hjelper deg å opprettholde en levestandard mer
            lik den du er vant med i dag.
          </p>
          <AkaDisabilityAdviceText
            need={need}
            adviceTexts={{
              ...adviceCard.adviceTexts,
              adviceUnavailableMessages: adviceCard.adviceUnavailableMessages,
            }}
            lang={lang}
          />
          <CollapsibleContainer isOpen={selected}>
            <AkaAdviceChartDisability
              formProps={formProps}
              need={need}
              adviceDetails={adviceDetails}
            />

            <AkaGroupLifeIncapacityOfWorkForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              priceIsChanging={priceIsChanging}
              priceIsLoading={priceIsLoading}
              adviceCard={adviceCard}
              lang={lang}
            />

            {(formProps.values.akaGroupLifeDeathCheckbox ||
              formProps.values.incapacityOfWorkCheckbox) &&
              adviceDetails.addCoInsured === BooleanFormType.TRUE && (
                <AkaGroupLifeIncapacityOfWorkCoInsuredForm
                  formProps={formProps}
                  recalculatePrice={recalculatePrice}
                  priceIsChanging={priceIsChanging}
                  priceIsLoading={priceIsLoading}
                  coInsuredBirthDate={adviceDetails.coInsuredBirthDate}
                />
              )}

            {adviceValueAkaGroupLifeIncapacity > 0 &&
              effectivePaymentRateAkaGroupLifeIncapacity < 1 && (
                <StyledAlert
                  className="EffectivePaymentInfo"
                  type="warning"
                  title={`A+ Gruppeliv uførekapital nedtrappes fra 51 år og i beregningene har vi brukt din alder om 5 år. ${adviceValueAkaGroupLifeIncapacity} G vil gi en utbetaling på ${getPriceFormatFromValue(
                    recommendedInsuranceSumAkaGroupLifeDeath,
                    VALUE_TYPE.NOK,
                  )}`}
                />
              )}

            {formProps.values.incapacityOfWorkCheckbox && (
              <AkaGroupLifeTimeLimitedDisabilityInterestForm
                formProps={formProps}
                need={need}
                recalculatePrice={recalculatePrice}
                priceIsChanging={priceIsChanging}
                priceIsLoading={priceIsLoading}
                adviceCard={adviceCard}
                lang={lang}
              />
            )}

            {adviceDetails.addCoInsured === BooleanFormType.TRUE &&
              formProps.values.incapacityOfWorkCoInsuredCheckbox && (
                <AkaGroupLifeTimeLimitedDisabilityInterestCoInsuredForm
                  formProps={formProps}
                  recalculatePrice={recalculatePrice}
                  priceIsChanging={priceIsChanging}
                  priceIsLoading={priceIsLoading}
                  coInsuredBirthDate={adviceDetails.coInsuredBirthDate}
                />
              )}

            <DisabilityForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              setPriceIsChanging={setPriceIsChanging}
              priceIsChanging={priceIsChanging}
              priceIsLoading={priceIsLoading}
              adviceCard={adviceCard}
              lang={lang}
            />

            {isAdvisor && formProps.values.disabilityInsuredSumCheckbox && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.DISABILITY_FLEX}
                sliderValue={currentSliderValue ?? 0}
                paymentRestriction={paymentRestriction}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
              />
            )}

            {isAdvisor && formProps.values.disabilityInsuredSumCheckbox && (
              <DisabilityPaymentAfter3Months
                adviceDetails={adviceDetails}
                formProps={formProps}
                need={need}
                recalculatePrice={recalculatePrice}
                adviceCard={adviceCard}
                lang={lang}
              />
            )}

            {formProps.values.disabilityInsuredSumCheckbox && (
              <DisabilityOneTimePaymentForm
                adviceDetails={adviceDetails}
                formProps={formProps}
                need={need}
                recalculatePrice={recalculatePrice}
                displayPrice
                priceIsChanging={priceIsChanging}
                priceIsLoading={priceIsLoading}
                adviceCard={adviceCard}
                lang={lang}
              />
            )}

            {formProps.values.disabilityInsuredSumCheckbox && (
              <WrapLgMarginBottom>
                <MarginLeftWrapper>
                  <ProductCoverages
                    selected
                    title={"Se betingelser og detaljer"}
                    readableInsuranceType={"Individuell uføreforsikring"}
                    backendInsuranceName={ProductBackendName.DISABILITY}
                    coverageGroup={mapInsuranceNameToAdviceCoverageName(
                      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
                    )}
                    // disabilityOneTimePayment={singlePayment} // TODO-AKA-ADVICE
                  />
                </MarginLeftWrapper>
              </WrapLgMarginBottom>
            )}
          </CollapsibleContainer>
          <AdviceCardComment
            commentId="disabilityAdviceComment"
            formProps={formProps}
            label="Notater om anbefaling og kundens valg av uføreforsikring"
          />
          <CardTotalPrice
            formProps={formProps}
            needName={NEED_NAME}
            priceIsLoading={priceIsLoading}
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
